import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type ReportDelivery from 'webapp/models-custom/report-delivery';
import type UserRecord from 'webapp/models/user';

export default class FilterSortReportDelivery extends FilterSort<ReportDelivery> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;

	override itemDomSelector = '.table-row';

	@tracked override sortProp = 'timestamp';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override searchProps: SearchProps = {
		title: 2,
		recipientsNames: 1,
	};

	override settingsNamePrefix = 'reportDeliveriesFilterSettings';

	constructor(options: FilterSortSettings<ReportDelivery>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Creator
		this.createFilter<number>({
			id: 4,
			label: 'Generated by',
			name: 'creator',
			selected: selectedFilters[4] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.userOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					const report = item.report;
					if (selectedValue === -1) {
						// exception: all others
						return !report?.isMine;
					} else {
						return report?.createdBy === selectedValue;
					}
				});
			},
		});

		// Tags (used only)
		this.createFilter<number>({
			id: 3,
			label: 'Tags',
			name: 'tag',
			selected: selectedFilters[3] as number | undefined,
			isMultiSelect: true,
			getOptions: () => {
				return this.tagOptions;
			},
			filterFn: function (item, selected) {
				return selected.some((selectedValue) => {
					const report = item.report;
					return !!report && report.tags.includes(selectedValue);
				});
			},
		});
	}

	get tagOptions(): FilterOption<number>[] {
		const values = this.mmReportsService.assignedTags,
			arrayCopy = Array.from(values) as FilterOption<number>[];
		if (arrayCopy.length) {
			arrayCopy.unshift({ id: 0, text: 'All' });
		}
		return arrayCopy;
	}

	get userOptions(): FilterOption<number>[] {
		const list: FilterOption<number>[] = [],
			allItemCountByCreator: Record<number, number> = {};

		this.allItems.forEach((item: ReportDelivery) => {
			const createdById = item.report?.createdBy;
			if (createdById) {
				if (!allItemCountByCreator[createdById]) {
					allItemCountByCreator[createdById] = 0;
				}
				allItemCountByCreator[createdById]++;
			}
		});

		this.mmUsersService.savedRecords.forEach((user: UserRecord) => {
			const id = +user.id!;
			if (allItemCountByCreator[id]) {
				list.push({
					id: id,
					text: user.name,
					img: user.avatar || '',
					used: allItemCountByCreator[id] || 0,
				});
			}
		});
		if (list.length) {
			list.unshift({ id: 0, text: 'All' });
		}
		return list;
	}
}
